// React
import { useEffect, useRef, useState } from "react";

// Store
import useSiteStore from '../../store/siteStore.js';

// Components
import {
    ClearOutlined, CloseOutlined, FileDownloadOutlined, AccessTimeOutlined, HotelOutlined, FavoriteBorderOutlined, ScaleOutlined,
    FitnessCenterOutlined, PsychologyOutlined, ScheduleOutlined, SpeedOutlined, ChatBubbleOutlineOutlined, AttachFileOutlined, AddOutlined, RemoveOutlined
} from "@mui/icons-material";
import { Box, Button, Chip, Divider, Grid, IconButton, InputAdornment, LinearProgress, Modal, Paper, Typography, Tooltip } from "@mui/material";
import { Stack } from "@mui/system";
import CustomSlider from "../custom/Slider.js";
import CustomTextField from "../custom/TextField.js";
import CustomSnackbar from '../custom/CustomSnackbar.js';

// Style
import { green, grey, red, yellow, blue, orange } from "@mui/material/colors/index.js";
import { darkTheme } from '../../styles/colorSchemes/index.js';

// Hooks
import { useAppTheme } from '../../hooks/useAppTheme.js';

// Other
import { formatDate, getISOWeekStart } from "../../utils/index.js";
import { addFeedback, addFeedbackFiles, updateFeedback, updateFeedbackFiles } from '../../service/httpService/feedback/index.js';

const GiveFeedback = (props) => {
    const { athlete, dayOfWeek, feedback, loaded, onClose, open, setLoaded, trainingSession } = props;
    const { weeklySchedule } = useSiteStore();
    const { isDarkMode } = useAppTheme();

    const [selectedMental, setSelectedMental] = useState(0);
    const [selectedPhysical, setSelectedPhysical] = useState(0);
    const [duration, setDuration] = useState(0);
    const [intensity, setIntensity] = useState(0);
    const [wakeUpTime, setWakeUpTime] = useState("00:00");
    const [sleepDuration, setSleepDuration] = useState(0.0);
    const [restingPulse, setRestingPulse] = useState(0);
    const [weight, setWeight] = useState(0.0);
    const [comment, setComment] = useState("");
    const [openFail, setOpenFail] = useState(false);
    const [files, setFiles] = useState([]);
    const [notTrained, setNotTrained] = useState(false);
    const [failMessage, setFailMessage] = useState("");
    const [isUpdated, setUpdated] = useState(false);
    const [openSuccess, setOpenSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const ref = useRef();

    useEffect(() => {
        if (feedback !== undefined && feedback !== "" && feedback.trainingsessionid !== undefined) {
            setUpdated(true);
            setSelectedMental(feedback.mentalExhaustion);
            setSelectedPhysical(feedback.physicalExhaustion);
            setIntensity(feedback.actualIntensity);
            setDuration(feedback.actualDuration);
            setComment(feedback.comment);
            let values = [];
            for (let f of feedback.files) {
                values.push({
                    file: new File([new Uint8Array(f.data.data).buffer], f.name),
                    isDeleted: f.isDeleted
                });
            }
            setFiles(values);
            if (athlete.hasExtendedFeedback === true) {
                setWakeUpTime(feedback.extendedFeedback.wakeUpTime);
                setSleepDuration(feedback.extendedFeedback.sleepDuration);
                setRestingPulse(feedback.extendedFeedback.restingPulse);
                setWeight(feedback.extendedFeedback.weight);
            }
        } else {
            setUpdated(false);
            setSelectedMental(0);
            setSelectedPhysical(0);
            setIntensity(0);
            setDuration(0);
            setComment("");
            setWakeUpTime("00:00");
            setSleepDuration(0.0);
            setRestingPulse(0);
            setWeight(0.0);
            setFiles([]);
        }
    }, [feedback, athlete.hasExtendedFeedback]);

    const handleFileSelected = (event) => {
        const MAX_FILE_SIZE = 5;
        let values = [...files];
        for (let file of event.target.files) {
            if (file !== undefined) {
                if (file.type === 'image/jpeg' || file.type === 'image/png' ||
                    file.type === 'video/mp4' || file.type === 'video/avi' ||
                    file.type === 'application/pdf' || file.type === 'video/mov') {
                    if ((file.size / 1024 / 1000) < MAX_FILE_SIZE) {
                        if (values.length + 1 < 6) {
                            for (let i = 0; i < values.length; i++) {
                                if (file.name === values[i].file.name) {
                                    values.splice(i, 1);
                                }
                            }
                            values.push({ file: file, isDeleted: false });
                        }
                    }
                }
            }
        }
        setFiles(values);
    }

    const onChangeMental = (mental) => (event) => {
        if (notTrained === true) {
            setNotTrained(false);
            setComment("");
        }
        setSelectedMental(mental);
    }

    const onChangePhysical = (mental) => (event) => {
        if (notTrained === true) {
            setNotTrained(false);
            setComment("");
        }
        setSelectedPhysical(mental);
    }

    const onChangeDuration = (event) => {
        if (notTrained === true) {
            setNotTrained(false);
            setComment("");
        }
        if (event.target.value >= 0 && Number.isInteger(Number(event.target.value)) === true) {
            setDuration(Number(event.target.value));
        }
    }

    const onChangeIntensity = (event, newValue) => {
        if (notTrained === true) {
            setNotTrained(false);
            setComment("");
        }
        setIntensity(newValue);
    }

    const onChangeComment = (event) => {
        setComment(event.target.value);
    }

    const onChangeWakeUpTime = (event) => {
        if (notTrained === true) {
            setNotTrained(false);
            setComment("");
        }
        setWakeUpTime(event.target.value);
    }

    const onChangeSleepDuration = (event) => {
        if (notTrained === true) {
            setNotTrained(false);
            setComment("");
        }
        if (event.target.value >= 0) {
            setSleepDuration(event.target.value);
        }
    }

    const onChangeRestingPulse = (event) => {
        if (notTrained === true) {
            setNotTrained(false);
            setComment("");
        }
        if (event.target.value >= 0 && Number.isInteger(Number(event.target.value)) === true) {
            setRestingPulse(Number(event.target.value));
        }
    }

    const onChangeWeight = (event) => {
        if (notTrained === true) {
            setNotTrained(false);
            setComment("");
        }
        if (event.target.value >= 0) {
            setWeight(event.target.value);
        }
    }

    const changeToNotTrained = () => {
        setNotTrained(true);
        setSelectedMental(0);
        setSelectedPhysical(0);
        setIntensity(0);
        setDuration(0);
        setComment("Nicht trainiert weil: ");

        if (athlete.hasExtendedFeedback === true) {
            setWakeUpTime("00:00");
            setSleepDuration(0.0);
            setRestingPulse(0);
            setWeight(0.0);
        }
    }

    const downloadFile = (file) => {
        const fileURL = window.URL.createObjectURL(new Blob([file]));
        let alink = document.createElement('a');
        alink.href = fileURL;
        alink.download = file.name;
        alink.click();
    }

    const save = () => {
        let formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append(`files`, new Blob([files[i].file]), files[i].file.name);
            formData.append(`feedbackFiles`, files[i].isDeleted);
        }

        if (notTrained === false) {
            if (selectedMental > 0) {
                if (selectedPhysical > 0) {
                    if (duration > 0 || [7, 8, 9].includes(trainingSession.trainingFocus)) {
                        if (intensity > 0 || [7, 8, 9].includes(trainingSession.trainingFocus)) {
                            // extended feedback
                            if (athlete.hasExtendedFeedback === true) {
                                if (wakeUpTime !== "00:00") {
                                    if (sleepDuration > 0) {
                                        if (weight > 0) {
                                            if (restingPulse > 0) {
                                                setLoaded(0);
                                            }
                                            else {
                                                setOpenFail(true);
                                                setFailMessage("Fehlerhaftes Feedback: Ruhepuls!");
                                                return;
                                            }
                                        }
                                        else {
                                            setOpenFail(true);
                                            setFailMessage("Fehlerhaftes Feedback: Gewicht!");
                                            return;
                                        }
                                    }
                                    else {
                                        setOpenFail(true);
                                        setFailMessage("Fehlerhaftes Feedback: Schlafdauer!");
                                        return;
                                    }
                                }
                                else {
                                    setOpenFail(true);
                                    setFailMessage("Fehlerhaftes Feedback: Weckzeit!");
                                    return;
                                }
                            }
                            //end of extended feedback
                        }
                        else {
                            setOpenFail(true);
                            setFailMessage("Fehlerhaftes Feedback: Tatsächliche Intensität!");
                            return;
                        }
                    }
                    else {
                        setOpenFail(true);
                        setFailMessage("Fehlerhaftes Feedback: Tatsächliche Dauer!");
                        return;
                    }
                }
                else {
                    setOpenFail(true);
                    setFailMessage("Fehlerhaftes Feedback: Körperliche Erschöpfung!");
                    return;
                }
            }
            else {
                setOpenFail(true);
                setFailMessage("Fehlerhaftes Feedback: Mentale Erschöpfung!");
                return;
            }
        }

        const feedback = {
            mentalExhaustion: selectedMental,
            physicalExhaustion: selectedPhysical,
            comment: comment,
            actualDuration: duration,
            actualIntensity: intensity,
            extendedFeedback: {
                wakeUpTime: wakeUpTime,
                sleepDuration: sleepDuration,
                weight: weight,
                restingPulse: restingPulse
            },
            trainingsessionid: trainingSession.trainingsessionid,
        }

        if (isUpdated === true) {
            updateFeedback(athlete.profile.email, feedback)
                .then(() => {
                    updateFeedbackFiles(trainingSession.trainingsessionid, formData, setLoaded)
                        .then(() => {
                            setLoaded(0);
                            setSuccessMessage('Rückmeldung erfolgreich aktualisiert!');
                            setOpenSuccess(true);
                            setTimeout(() => {
                                setOpenSuccess(false);
                                onClose();
                                if (props.onSuccess) props.onSuccess();
                            }, 1200);
                        })
                        .catch(() => {
                            setOpenFail(true);
                            setFailMessage("Aktualisieren der Dateianhänge fehlgeschlagen!");
                            setLoaded(0);
                        });
                })
                .catch(() => {
                    if (athlete.hasExtendedFeedback === true) {
                        setFailMessage("Aktualisieren der erweiterten Rückmeldung fehlgeschlagen!");
                    }
                    else {
                        setFailMessage("Aktualisieren der Rückmeldung fehlgeschlagen!");
                    }
                    setOpenFail(true);
                    setLoaded(0);
                });
        }
        else {
            addFeedback(athlete.profile.email, feedback)
                .then(() => {
                    addFeedbackFiles(trainingSession.trainingsessionid, formData, setLoaded)
                        .then(() => {
                            setLoaded(0);
                            setSuccessMessage('Rückmeldung erfolgreich erstellt!');
                            setOpenSuccess(true);
                            setTimeout(() => {
                                setOpenSuccess(false);
                                onClose();
                                if (props.onSuccess) props.onSuccess();
                            }, 1200);
                        })
                        .catch(() => {
                            setOpenFail(true);
                            setFailMessage("Erstellen der Dateianhänge fehlgeschlagen!");
                            setLoaded(0);
                        });
                })
                .catch(() => {
                    if (athlete.hasExtendedFeedback === true) {
                        setFailMessage("Erstellen der erweiterten Rückmeldung fehlgeschlagen!");
                    }
                    else {
                        setFailMessage("Erstellen der Rückmeldung fehlgeschlagen!");
                    }
                    setOpenFail(true);
                    setLoaded(0);
                });
        }
    }

    const primaryColor = isDarkMode ? orange[500] : '#1976d2';

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                pb: 2,
                pt: 2,
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'auto',
                '@media (max-width: 900px)': {
                    alignItems: 'flex-start',
                    p: 0,
                    overflow: 'auto'
                }
            }}
        >
            <Paper
                elevation={8}
                sx={{
                    width: { xs: '95%', sm: '90%', md: '85%', lg: '80%', xl: '75%' },
                    maxWidth: '1400px',
                    p: { xs: 2, sm: 3, md: 4 },
                    borderRadius: 3,
                    border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : 'none',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    backgroundColor: isDarkMode ? darkTheme.bg : '#fafafa',
                    position: 'relative',
                    '@media (max-width: 900px)': {
                        width: '100%',
                        minHeight: '100vh',
                        maxHeight: '100vh',
                        borderRadius: 0,
                        m: 0
                    }
                }}
            >
                {/* Close Button */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 10,
                        bgcolor: isDarkMode ? darkTheme.cardBg : 'rgba(255, 255, 255, 0.9)',
                        color: isDarkMode ? darkTheme.textSecondary : undefined,
                        '&:hover': {
                            bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.05)',
                            transform: 'rotate(90deg)',
                            transition: 'transform 0.3s ease'
                        }
                    }}
                >
                    <CloseOutlined />
                </IconButton>

                <CustomSnackbar
                    open={openFail}
                    onClose={() => setOpenFail(false)}
                    message={failMessage}
                    severity="error"
                    autoHideDuration={4000}
                />
                <CustomSnackbar
                    open={openSuccess}
                    onClose={() => setOpenSuccess(false)}
                    message={successMessage}
                    severity="success"
                    autoHideDuration={3000}
                />

                <Box sx={{ mb: 2 }}>
                    <Typography
                        variant="h5"
                        sx={{
                            fontWeight: 600,
                            color: isDarkMode ? darkTheme.textPrimary : grey[800],
                        }}
                    >
                        {athlete.hasExtendedFeedback === false ? 'Rückmeldung geben' : 'Erweiterte Rückmeldung geben'}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: isDarkMode ? darkTheme.textSecondary : grey[500],
                            }}
                        >
                            {formatDate(getISOWeekStart(weeklySchedule.calendarWeek, new Date().getFullYear(), dayOfWeek - 1))}
                        </Typography>

                        {feedback && feedback.lastEdited && (
                            <>
                                <Typography variant="body2" sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[400] }}>•</Typography>
                                <Typography
                                    sx={{
                                        fontStyle: 'italic',
                                        color: isDarkMode ? darkTheme.textSecondary : grey[500],
                                        fontSize: '0.875rem'
                                    }}
                                    variant="body2"
                                >
                                    Zuletzt geändert: {formatDate(feedback.lastEdited)}
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>

                <Divider sx={{ mb: 3, borderColor: isDarkMode ? darkTheme.cardBorder : undefined }} />

                <Grid container spacing={3}>
                    {athlete.hasExtendedFeedback === true && (
                        <>
                            <Grid item xs={12}>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 600,
                                        color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                        mb: 1,
                                        fontSize: '1.1rem'
                                    }}
                                >
                                    Gesundheitsdaten
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <CustomTextField
                                    fullWidth
                                    label="Weckzeit"
                                    value={wakeUpTime}
                                    onChange={onChangeWakeUpTime}
                                    type="time"
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <AccessTimeOutlined sx={{ mr: 1, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            '& input': {
                                                color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                '&::-webkit-calendar-picker-indicator': {
                                                    filter: isDarkMode ? 'invert(1)' : undefined,
                                                }
                                            },
                                            '&:hover fieldset': {
                                                borderColor: primaryColor,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: primaryColor,
                                            },
                                            '& fieldset': {
                                                borderColor: isDarkMode ? darkTheme.cardBorder : undefined,
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: isDarkMode ? darkTheme.textSecondary : undefined,
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <CustomTextField
                                    fullWidth
                                    label="Schlafdauer"
                                    value={sleepDuration}
                                    onChange={onChangeSleepDuration}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <HotelOutlined sx={{ mr: 1, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                        ),
                                        endAdornment:
                                            <InputAdornment position="end">
                                                <Typography sx={{ color: isDarkMode ? darkTheme.textSecondary : undefined }}>Stunden</Typography>
                                            </InputAdornment>,
                                        sx: { alignItems: 'center' }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            '& input': {
                                                color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: primaryColor,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: primaryColor,
                                            },
                                            '& fieldset': {
                                                borderColor: isDarkMode ? darkTheme.cardBorder : undefined,
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: isDarkMode ? darkTheme.textSecondary : undefined,
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <CustomTextField
                                    fullWidth
                                    label="Ruhepuls"
                                    value={restingPulse}
                                    onChange={onChangeRestingPulse}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <FavoriteBorderOutlined sx={{ mr: 1, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                        ),
                                        endAdornment:
                                            <InputAdornment position="end">
                                                <Typography sx={{ color: isDarkMode ? darkTheme.textSecondary : undefined }}>Bpm</Typography>
                                            </InputAdornment>,
                                        sx: { alignItems: 'center' }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            '& input': {
                                                color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: primaryColor,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: primaryColor,
                                            },
                                            '& fieldset': {
                                                borderColor: isDarkMode ? darkTheme.cardBorder : undefined,
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: isDarkMode ? darkTheme.textSecondary : undefined,
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <CustomTextField
                                    fullWidth
                                    label="Gewicht"
                                    value={weight}
                                    onChange={onChangeWeight}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <ScaleOutlined sx={{ mr: 1, color: isDarkMode ? darkTheme.textSecondary : 'action.active' }} />
                                        ),
                                        endAdornment:
                                            <InputAdornment position="end">
                                                <Typography sx={{ color: isDarkMode ? darkTheme.textSecondary : undefined }}>Kg</Typography>
                                            </InputAdornment>,
                                        sx: { alignItems: 'center' }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            '& input': {
                                                color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            },
                                            '&:hover fieldset': {
                                                borderColor: primaryColor,
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: primaryColor,
                                            },
                                            '& fieldset': {
                                                borderColor: isDarkMode ? darkTheme.cardBorder : undefined,
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: isDarkMode ? darkTheme.textSecondary : undefined,
                                        }
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                mb: 2,
                                fontSize: '1.1rem'
                            }}
                        >
                            Erschöpfung
                        </Typography>

                        <Stack spacing={2}>
                            <Box
                                sx={{
                                    p: 2,
                                    border: `2px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                    borderRadius: 2,
                                    backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: primaryColor,
                                        boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <FitnessCenterOutlined sx={{ mr: 1, color: primaryColor, fontSize: '1.25rem' }} />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: isDarkMode ? darkTheme.textPrimary : grey[700]
                                        }}
                                    >
                                        Körperliche Erschöpfung
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                                    <Box
                                        onClick={onChangePhysical(1)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: 0.75,
                                            borderRadius: selectedPhysical === 1 ? 16 : 2,
                                            backgroundColor: selectedPhysical === 1 ? `${green[100]}` : 'transparent',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: `${green[100]}`,
                                                borderRadius: 16,
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            backgroundColor: green[500],
                                            boxShadow: selectedPhysical === 1 ? `0 0 0 3px ${green[200]}` : 'none',
                                            transform: selectedPhysical === 1 ? 'scale(1.05)' : 'scale(1)',
                                            transition: 'all 0.2s ease',
                                        }} />
                                        <Typography variant="caption" sx={{ mt: 0.5, color: selectedPhysical === 1 ? green[700] : grey[500], fontWeight: selectedPhysical === 1 ? 600 : 500, fontSize: '0.7rem' }}>Gut</Typography>
                                    </Box>

                                    <Box
                                        onClick={onChangePhysical(2)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: 0.75,
                                            borderRadius: selectedPhysical === 2 ? 16 : 2,
                                            backgroundColor: selectedPhysical === 2 ? `${yellow[100]}` : 'transparent',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: `${yellow[100]}`,
                                                borderRadius: 16,
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            backgroundColor: yellow[600],
                                            boxShadow: selectedPhysical === 2 ? `0 0 0 3px ${yellow[200]}` : 'none',
                                            transform: selectedPhysical === 2 ? 'scale(1.05)' : 'scale(1)',
                                            transition: 'all 0.2s ease',
                                        }} />
                                        <Typography variant="caption" sx={{ mt: 0.5, color: selectedPhysical === 2 ? yellow[800] : grey[500], fontWeight: selectedPhysical === 2 ? 600 : 500, fontSize: '0.7rem' }}>Mittel</Typography>
                                    </Box>

                                    <Box
                                        onClick={onChangePhysical(3)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: 0.75,
                                            borderRadius: selectedPhysical === 3 ? 16 : 2,
                                            backgroundColor: selectedPhysical === 3 ? `${red[100]}` : 'transparent',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: `${red[100]}`,
                                                borderRadius: 16,
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            backgroundColor: red[500],
                                            boxShadow: selectedPhysical === 3 ? `0 0 0 3px ${red[200]}` : 'none',
                                            transform: selectedPhysical === 3 ? 'scale(1.05)' : 'scale(1)',
                                            transition: 'all 0.2s ease',
                                        }} />
                                        <Typography variant="caption" sx={{ mt: 0.5, color: selectedPhysical === 3 ? red[700] : grey[500], fontWeight: selectedPhysical === 3 ? 600 : 500, fontSize: '0.7rem' }}>Hoch</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    p: 2,
                                    border: `2px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                    borderRadius: 2,
                                    backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        borderColor: primaryColor,
                                        boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <PsychologyOutlined sx={{ mr: 1, color: primaryColor, fontSize: '1.25rem' }} />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: isDarkMode ? darkTheme.textPrimary : grey[700]
                                        }}
                                    >
                                        Mentale Erschöpfung
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                                    <Box
                                        onClick={onChangeMental(1)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: 0.75,
                                            borderRadius: selectedMental === 1 ? 16 : 2,
                                            backgroundColor: selectedMental === 1 ? `${green[100]}` : 'transparent',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: `${green[100]}`,
                                                borderRadius: 16,
                                            }

                                        }}
                                    >
                                        <Box sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            backgroundColor: green[500],
                                            boxShadow: selectedMental === 1 ? `0 0 0 3px ${green[200]}` : 'none',
                                            transform: selectedMental === 1 ? 'scale(1.05)' : 'scale(1)',
                                            transition: 'all 0.2s ease',
                                        }} />
                                        <Typography variant="caption" sx={{ mt: 0.5, color: selectedMental === 1 ? green[700] : grey[500], fontWeight: selectedMental === 1 ? 600 : 500, fontSize: '0.7rem' }}>Gut</Typography>
                                    </Box>

                                    <Box
                                        onClick={onChangeMental(2)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: 0.75,
                                            borderRadius: selectedMental === 2 ? 16 : 2,
                                            backgroundColor: selectedMental === 2 ? `${yellow[100]}` : 'transparent',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: `${yellow[100]}`,
                                                borderRadius: 16,
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            backgroundColor: yellow[600],
                                            boxShadow: selectedMental === 2 ? `0 0 0 3px ${yellow[200]}` : 'none',
                                            transform: selectedMental === 2 ? 'scale(1.05)' : 'scale(1)',
                                            transition: 'all 0.2s ease',
                                        }} />
                                        <Typography variant="caption" sx={{ mt: 0.5, color: selectedMental === 2 ? yellow[800] : grey[500], fontWeight: selectedMental === 2 ? 600 : 500, fontSize: '0.7rem' }}>Mittel</Typography>
                                    </Box>

                                    <Box
                                        onClick={onChangeMental(3)}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            cursor: 'pointer',
                                            padding: 0.75,
                                            borderRadius: selectedMental === 3 ? 16 : 2,
                                            backgroundColor: selectedMental === 3 ? `${red[100]}` : 'transparent',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                backgroundColor: `${red[100]}`,
                                                borderRadius: 16,
                                            }
                                        }}
                                    >
                                        <Box sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            backgroundColor: red[500],
                                            boxShadow: selectedMental === 3 ? `0 0 0 3px ${red[200]}` : 'none',
                                            transform: selectedMental === 3 ? 'scale(1.05)' : 'scale(1)',
                                            transition: 'all 0.2s ease',
                                        }} />
                                        <Typography variant="caption" sx={{ mt: 0.5, color: selectedMental === 3 ? red[700] : grey[500], fontWeight: selectedMental === 3 ? 600 : 500, fontSize: '0.7rem' }}>Hoch</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: isDarkMode ? darkTheme.textPrimary : grey[700],
                                mb: 2,
                                fontSize: '1.1rem'
                            }}
                        >
                            Trainingsdetails
                        </Typography>

                        <Stack spacing={2}>
                            <Box
                                sx={{
                                    p: 1.5,
                                    border: `2px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                    borderRadius: 2,
                                    backgroundColor: [7, 8, 9].includes(trainingSession.trainingFocus)
                                        ? (isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)')
                                        : (isDarkMode ? darkTheme.cardBg : 'white'),
                                    transition: 'all 0.3s ease',
                                    '&:hover': [7, 8, 9].includes(trainingSession.trainingFocus) ? {} : {
                                        borderColor: primaryColor,
                                        boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3.8 }}>
                                    <ScheduleOutlined sx={{ mr: 1, color: [7, 8, 9].includes(trainingSession.trainingFocus) ? (isDarkMode ? 'rgba(255,255,255,0.3)' : 'action.disabled') : primaryColor, fontSize: '1.25rem' }} />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mt: 0.25,
                                            fontWeight: 600,
                                            color: [7, 8, 9].includes(trainingSession.trainingFocus)
                                                ? (isDarkMode ? darkTheme.textSecondary : grey[400])
                                                : (isDarkMode ? darkTheme.textPrimary : grey[700])
                                        }}
                                    >
                                        Tatsächliche Dauer
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <IconButton
                                        size="small"
                                        disabled={[7, 8, 9].includes(trainingSession.trainingFocus) || duration <= 0}
                                        onClick={() => setDuration(Math.max(0, duration - 1))}
                                        sx={{
                                            backgroundColor: isDarkMode ? darkTheme.cardBg : grey[200],
                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            width: 32,
                                            height: 32,
                                            '&:hover': {
                                                backgroundColor: isDarkMode ? darkTheme.cardHover : grey[300]
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : grey[100],
                                                color: isDarkMode ? darkTheme.textSecondary : undefined
                                            }
                                        }}
                                    >
                                        <RemoveOutlined sx={{ fontSize: '1rem' }} />
                                    </IconButton>

                                    <CustomTextField
                                        disabled={[7, 8, 9].includes(trainingSession.trainingFocus)}
                                        value={duration}
                                        onChange={onChangeDuration}
                                        variant="outlined"
                                        size="small"
                                        inputProps={{
                                            style: {
                                                textAlign: 'center',
                                                fontWeight: 600,
                                                fontSize: '1.1rem',
                                                color: isDarkMode ? '#fff' : undefined
                                            }
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Typography variant="body2" sx={{ color: isDarkMode ? darkTheme.textSecondary : grey[600], fontSize: '0.8rem' }}>
                                                        Min
                                                    </Typography>
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            flex: 1,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                                backgroundColor: isDarkMode ? darkTheme.cardBg : undefined,
                                                '& input': {
                                                    color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                },
                                                '&:hover:not(.Mui-disabled) fieldset': {
                                                    borderColor: primaryColor,
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: primaryColor,
                                                },
                                                '& fieldset': {
                                                    borderColor: isDarkMode ? darkTheme.cardBorder : grey[300]
                                                }
                                            }
                                        }}
                                    />

                                    <IconButton
                                        size="small"
                                        disabled={[7, 8, 9].includes(trainingSession.trainingFocus)}
                                        onClick={() => setDuration(duration + 1)}
                                        sx={{
                                            backgroundColor: isDarkMode ? darkTheme.cardBg : grey[200],
                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            width: 32,
                                            height: 32,
                                            '&:hover': {
                                                backgroundColor: isDarkMode ? darkTheme.cardHover : grey[300]
                                            },
                                            '&.Mui-disabled': {
                                                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : grey[100],
                                                color: isDarkMode ? darkTheme.textSecondary : undefined
                                            }
                                        }}
                                    >
                                        <AddOutlined sx={{ fontSize: '1rem' }} />
                                    </IconButton>
                                </Box>
                            </Box>

                            <Box
                                sx={{
                                    p: 1.5,
                                    border: `2px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                    borderRadius: 2,
                                    backgroundColor: [7, 8, 9].includes(trainingSession.trainingFocus)
                                        ? (isDarkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)')
                                        : (isDarkMode ? darkTheme.cardBg : 'white'),
                                    transition: 'all 0.3s ease',
                                    '&:hover': [7, 8, 9].includes(trainingSession.trainingFocus) ? {} : {
                                        borderColor: primaryColor,
                                        boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <SpeedOutlined sx={{ mr: 1, color: [7, 8, 9].includes(trainingSession.trainingFocus) ? (isDarkMode ? 'rgba(255,255,255,0.3)' : 'action.disabled') : primaryColor, fontSize: '1.25rem' }} />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontWeight: 600,
                                            color: [7, 8, 9].includes(trainingSession.trainingFocus)
                                                ? (isDarkMode ? darkTheme.textSecondary : grey[400])
                                                : (isDarkMode ? darkTheme.textPrimary : grey[700])
                                        }}
                                    >
                                        Tatsächliche Intensität: {intensity}/10
                                    </Typography>
                                </Box>

                                <Box sx={{ position: 'relative', width: '95%', marginLeft: 1.2 }}>
                                    <CustomSlider
                                        getAriaValueText={(value) => {
                                            return `${value}`
                                        }}
                                        disabled={[7, 8, 9].includes(trainingSession.trainingFocus)}
                                        valueLabelDisplay="auto"
                                        step={1}
                                        max={10}
                                        marks={[
                                            { value: 0, label: '0' },
                                            { value: 5, label: '5' },
                                            { value: 10, label: '10' }
                                        ]}
                                        value={intensity}
                                        onChange={onChangeIntensity}
                                        sx={{
                                            height: 8,
                                            '& .MuiSlider-thumb': {
                                                width: 24,
                                                height: 24,
                                                backgroundColor: 'white',
                                                border: `3px solid ${primaryColor}`,
                                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                                zIndex: 3,
                                                '&:hover, &.Mui-focusVisible': {
                                                    boxShadow: `0 0 0 8px ${primaryColor}20`,
                                                },
                                                '&.Mui-active': {
                                                    boxShadow: `0 0 0 12px ${primaryColor}30`,
                                                },
                                            },
                                            '& .MuiSlider-track': {
                                                background: `linear-gradient(90deg, ${green[400]} 0%, ${yellow[500]} 50%, ${red[500]} 100%)`,
                                                backgroundSize: `${1000 / Math.max(intensity, 1)}% 100%`,
                                                border: 'none',
                                                zIndex: 2,
                                            },
                                            '& .MuiSlider-rail': {
                                                backgroundColor: isDarkMode ? darkTheme.cardBorder : grey[200],
                                                opacity: 1,
                                                zIndex: 1,
                                            },
                                            '&.Mui-disabled': {
                                                '& .MuiSlider-thumb': {
                                                    backgroundColor: isDarkMode ? darkTheme.cardBg : grey[300],
                                                    border: `3px solid ${isDarkMode ? 'rgba(255,255,255,0.3)' : grey[400]}`,
                                                },
                                                '& .MuiSlider-track': {
                                                    background: isDarkMode ? 'rgba(255,255,255,0.1)' : grey[300],
                                                },
                                            },
                                            '& .MuiSlider-valueLabel': {
                                                backgroundColor: primaryColor,
                                                borderRadius: '8px',
                                                padding: '4px 8px',
                                                fontSize: '0.875rem',
                                                fontWeight: 600,
                                            },
                                            '& .MuiSlider-mark': {
                                                backgroundColor: grey[400],
                                                height: 8,
                                                width: 2,
                                                borderRadius: 1,
                                            },
                                            '& .MuiSlider-markActive': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            },
                                            '& .MuiSlider-markLabel': {
                                                fontSize: '0.75rem',
                                                color: isDarkMode ? darkTheme.textSecondary : grey[500],
                                                fontWeight: 500,
                                            },
                                        }}
                                    />
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                p: 2,
                                border: `2px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                borderRadius: 2,
                                backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: primaryColor,
                                    boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <ChatBubbleOutlineOutlined sx={{ mr: 1, color: primaryColor }} />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: isDarkMode ? darkTheme.textPrimary : grey[700]
                                    }}
                                >
                                    Kommentar
                                </Typography>
                            </Box>

                            <CustomTextField
                                fullWidth
                                multiline
                                variant="outlined"
                                minRows={2}
                                maxRows={4}
                                type="text"
                                placeholder="Hier können Sie Ihr Feedback eingeben..."
                                value={comment}
                                onChange={onChangeComment}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        backgroundColor: isDarkMode ? darkTheme.cardBg : undefined,
                                        color: isDarkMode ? darkTheme.textPrimary : undefined,
                                        '& textarea': {
                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                            '&::placeholder': {
                                                color: isDarkMode ? darkTheme.textSecondary : undefined,
                                                opacity: 1,
                                            }
                                        },
                                        '&:hover fieldset': {
                                            borderColor: primaryColor,
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: primaryColor,
                                        },
                                        '& fieldset': {
                                            borderColor: isDarkMode ? darkTheme.cardBorder : grey[300]
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                p: 2,
                                border: `2px solid ${isDarkMode ? darkTheme.cardBorder : grey[300]}`,
                                borderRadius: 2,
                                backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                flexDirection: 'column',
                                '&:hover': {
                                    borderColor: primaryColor,
                                    boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <AttachFileOutlined sx={{ mr: 1, color: primaryColor }} />
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 600,
                                        color: isDarkMode ? darkTheme.textPrimary : grey[700]
                                    }}
                                >
                                    Dateianhänge
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        ml: 1,
                                        color: isDarkMode ? darkTheme.textSecondary : grey[500],
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    (max. 5 Dateien je max. 5 MB)
                                </Typography>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                justifyContent: files.length === 0 ? 'center' : 'flex-start',
                                gap: 1.5,
                                flex: 1,
                                py: files.length === 0 ? 2 : 0
                            }}>
                                <IconButton
                                    onClick={() => ref.current?.click()}
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        border: `2px dashed ${primaryColor}`,
                                        color: primaryColor,
                                        backgroundColor: `${primaryColor}08`,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: `${primaryColor}15`,
                                            borderColor: primaryColor,
                                            transform: 'scale(1.05)',
                                        }
                                    }}
                                >
                                    <AddOutlined sx={{ fontSize: '1.5rem' }} />
                                </IconButton>

                                {files.map((file, index) => {
                                    return (
                                        <Stack key={index} direction="row">
                                            <Chip
                                                sx={{
                                                    height: 42,
                                                    borderTopRightRadius: 0,
                                                    borderBottomRightRadius: 0,
                                                    backgroundColor: isDarkMode ? darkTheme.cardHover : grey[100],
                                                    color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                    fontWeight: 500,
                                                    maxWidth: '200px',
                                                    '& .MuiChip-label': {
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }
                                                }}
                                                label={`${file.file.name}`}
                                            />
                                            <Tooltip title="Datei entfernen">
                                                <span>
                                                    <IconButton
                                                        disabled={file.isDeleted}
                                                        sx={{
                                                            '&.Mui-disabled': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : grey[200] },
                                                            height: 42,
                                                            width: 42,
                                                            borderRadius: 0,
                                                            bgcolor: isDarkMode ? darkTheme.cardBg : grey[200],
                                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                            '&:hover': {
                                                                bgcolor: isDarkMode ? darkTheme.cardHover : grey[300]
                                                            }
                                                        }}
                                                        onClick={() => {
                                                            let v = [...files]
                                                            v.splice(index, 1);
                                                            setFiles(v);
                                                        }}
                                                    >
                                                        <ClearOutlined />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>

                                            <Tooltip title="Datei herunterladen">
                                                <span>
                                                    <IconButton
                                                        disabled={file.isDeleted}
                                                        sx={{
                                                            '&.Mui-disabled': { bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : grey[200] },
                                                            height: 42,
                                                            width: 42,
                                                            borderTopLeftRadius: 0,
                                                            borderBottomLeftRadius: 0,
                                                            borderTopRightRadius: 1,
                                                            borderBottomRightRadius: 1,
                                                            bgcolor: isDarkMode ? darkTheme.cardBg : grey[200],
                                                            color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                            '&:hover': {
                                                                bgcolor: isDarkMode ? darkTheme.cardHover : grey[300]
                                                            }
                                                        }}
                                                        onClick={() => downloadFile(file.file)}
                                                    >
                                                        <FileDownloadOutlined />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    )
                                })}
                            </Box>
                        </Box>
                    </Grid>
                </Grid>

                <input style={{ display: 'none' }} type='file' accept='image/png, image/jpeg, video/mp4, video/avi, video/mov, application/pdf' multiple onChange={handleFileSelected} ref={ref} />

                <Divider sx={{ mt: 3, mb: 1, border: 'none' }} />

                {loaded > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <LinearProgress
                            sx={{
                                height: 6,
                                borderRadius: 3,
                                backgroundColor: grey[200],
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: primaryColor
                                }
                            }}
                            variant="determinate"
                            value={loaded}
                        />
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                        onClick={save}
                        variant="contained"
                        sx={{
                            minWidth: 140,
                            height: 44,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: isDarkMode ? '#1a1a1a' : 'white',
                            backgroundColor: primaryColor,
                            boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.3)' : '0 2px 8px rgba(25, 118, 210, 0.3)',
                            '&:hover': {
                                backgroundColor: isDarkMode ? orange[600] : blue[700],
                                boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.4)' : '0 4px 12px rgba(25, 118, 210, 0.4)',
                                transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        Speichern
                    </Button>

                    <Button
                        onClick={changeToNotTrained}
                        variant="outlined"
                        sx={{
                            minWidth: 140,
                            height: 44,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '1rem',
                            color: red[isDarkMode ? 400 : 600],
                            borderColor: red[400],
                            '&:hover': {
                                backgroundColor: isDarkMode ? 'rgba(244, 67, 54, 0.15)' : red[50],
                                borderColor: red[600],
                                transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s ease'
                        }}
                    >
                        Nicht trainiert
                    </Button>
                </Box>
            </Paper>
        </Modal>
    )
}

export default GiveFeedback;